import { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useDocumentTitle } from '@mantine/hooks';
import {
  Container,
  Card,
  Group,
  Text,
  Flex,
  Box,
  Grid,
  Skeleton,
  createStyles,
  ThemeIcon,
} from '@mantine/core';
import {
  IconHelp,
  IconClock,
  IconEye,
  IconWorld,
  IconHandClick,
  IconArrowsVertical,
  IconVideo,
  IconChartArea,
  IconFilter,
} from '@tabler/icons-react';

import { HeaderMenu } from './HeaderMenu';
import {
  SelectMemberWebsiteList,
  SelectFilterType,
  SelectTimePicker,
  SelectTooltip,
  VisiLoader,
} from '../GeneralComponent/GeneralComponent';
import DashBoardApi from '../../api/DashBoardApi';
import { visiUserSession } from '../../GeneralFunction';

const useStyles = createStyles((theme) => {
  const dark = theme.colorScheme === 'dark';
  return {
    pageTitle: {
      fontSize: 14,
      fontWeight: 600,
      letterSpacing: '0.04em',
      textTransform: 'uppercase' as const,
      color: dark ? theme.white : '#1A1A1A',
    },
    sectionLabel: {
      fontSize: 11,
      fontWeight: 600,
      letterSpacing: '0.06em',
      textTransform: 'uppercase' as const,
      color: dark ? theme.colors.dark[1] : '#6b7280',
    },
    filterBar: {
      backgroundColor: dark ? theme.colors.dark[6] : '#f8f9fa',
      border: `1px solid ${dark ? theme.colors.dark[4] : '#e9ecef'}`,
      borderRadius: theme.radius.md,
      padding: '12px 14px',
    },
    statTile: {
      backgroundColor: dark ? theme.colors.dark[6] : '#ffffff',
      border: `1px solid ${dark ? theme.colors.dark[4] : '#e9ecef'}`,
      borderRadius: theme.radius.md,
      padding: '18px 20px',
      transition: 'border-color 120ms ease, box-shadow 120ms ease',
      '&:hover': {
        borderColor: dark ? theme.colors.dark[3] : '#d0d7de',
        boxShadow: dark ? 'none' : '0 1px 3px rgba(0,0,0,0.06)',
      },
    },
    statLabel: {
      fontSize: 11,
      fontWeight: 600,
      letterSpacing: '0.06em',
      textTransform: 'uppercase' as const,
      color: dark ? theme.colors.dark[1] : '#6b7280',
    },
    statValue: {
      fontSize: 28,
      fontWeight: 700,
      color: dark ? theme.white : '#1A1A1A',
      lineHeight: 1.1,
    },
    statValueSm: {
      fontSize: 22,
      fontWeight: 700,
      color: dark ? theme.white : '#1A1A1A',
      lineHeight: 1.1,
    },
    domainBar: {
      backgroundColor: dark ? theme.colors.dark[7] : '#f1f5f9',
      border: `1px solid ${dark ? theme.colors.dark[4] : '#e2e8f0'}`,
      borderRadius: theme.radius.md,
      padding: '10px 14px',
    },
    miniLabel: {
      fontSize: 12,
      color: dark ? theme.colors.dark[1] : '#6b7280',
    },
  };
});

const filterTypeUsesVisits = (filterType: string) =>
  filterType === '1' || filterType === '2' || filterType === '3';

const filterTotalLabel = (filterType: string) => {
  switch (filterType) {
    case '1':
      return 'Total Account Visits';
    case '2':
      return 'Total Test Visits';
    case '3':
      return 'Total Campaign Visits';
    case '5':
      return 'Total Heatmap Clicks';
    case '6':
      return 'Total Scrollmap Views';
    case '7':
      return 'Total Recordings';
    default:
      return 'Total Account Visits';
  }
};

const filterDomainLabel = (filterType: string) => {
  switch (filterType) {
    case '1':
      return 'Domain Visits';
    case '2':
      return 'Domain Test Visits';
    case '3':
      return 'Domain Campaign Visits';
    case '5':
      return 'Domain Heatmap Clicks';
    case '6':
      return 'Domain Scrollmap Views';
    case '7':
      return 'Domain Recordings';
    default:
      return 'Domain Visits';
  }
};

const formatNumber = (value: any) => {
  const num = Number(value || 0);
  if (Number.isNaN(num)) {
    return '0';
  }
  return num.toLocaleString('en-US');
};

const StatTile = ({
  classes,
  icon,
  iconColor,
  label,
  value,
  loading,
  small = false,
}: any) => (
  <Box className={classes.statTile}>
    <Group spacing="md" noWrap align="center">
      <ThemeIcon size={42} radius="md" variant="light" color={iconColor}>
        {icon}
      </ThemeIcon>
      <Box style={{ flex: 1, minWidth: 0 }}>
        <Text className={classes.statLabel} mb={4}>
          {label}
        </Text>
        {loading ? (
          <Skeleton height={small ? 22 : 28} width={140} radius="sm" />
        ) : (
          <Text className={small ? classes.statValueSm : classes.statValue}>
            {value}
          </Text>
        )}
      </Box>
    </Group>
  </Box>
);

export function SystemUsage() {
  useDocumentTitle('Visiopt - System Usage');
  const navigate = useNavigate();
  const { classes } = useStyles();

  const [selectedHeaderWebsite, setSelectedHeaderWebsite] = useState<string>(
    visiUserSession()?.website || ''
  );
  const [selectedDomainWebsite, setSelectedDomainWebsite] = useState<string>('');
  const [selectedFilterType, setSelectedFilterType] = useState<string>('1');
  const [selectedTimePicker, setSelectedTimePicker] = useState<any>(
    visiUserSession()?.time !== undefined ? visiUserSession().time : -3
  );
  const [selectedTimePickerFromDate, setSelectedTimePickerFromDate] = useState<string>('');
  const [selectedTimePickerToDate, setSelectedTimePickerToDate] = useState<string>('');

  const [usageData, setUsageData] = useState<any>(null);
  const [isLoaderVisible, setIsLoaderVisible] = useState<boolean>(false);
  const [tilesLoading, setTilesLoading] = useState<boolean>(true);

  const [filterTypeLoaded, setFilterTypeLoaded] = useState<boolean>(false);
  const [timePickerLoaded, setTimePickerLoaded] = useState<boolean>(false);
  const [domainLoaded, setDomainLoaded] = useState<boolean>(false);

  const [firstLoadDone, setFirstLoadDone] = useState<boolean>(false);

  const prevTimePickerRef = useRef<any>(selectedTimePicker);

  const [showHelpModal, setShowHelpModal] = useState<boolean>(false);
  const [helpTargetId, setHelpTargetId] = useState<number>(0);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
  const helpIconRef = useRef<any>(null);

  const handleHeaderMenuComponentChange = (value: any) => {
    setSelectedHeaderWebsite(value);
  };

  const handleDomainWebsiteChange = (value: any) => {
    setSelectedDomainWebsite(value);
    if (value) {
      setDomainLoaded(true);
    }
  };

  const handleFilterTypeChange = (value: any) => {
    setSelectedFilterType(value);
    if (value) {
      setFilterTypeLoaded(true);
    }
  };

  const handleTimePickerChange = (value: any) => {
    setSelectedTimePicker(value);
    if (value !== undefined && value !== null && value !== '') {
      setTimePickerLoaded(true);
    }
  };

  const handleTimePickerFromDateChange = (value: any) => {
    setSelectedTimePickerFromDate(value || '');
  };

  const handleTimePickerToDateChange = (value: any) => {
    setSelectedTimePickerToDate(value || '');
  };

  const handleHelpClick = (id: number) => {
    setShowHelpModal(true);
    setHelpTargetId(id);
    if (helpIconRef.current) {
      const { top, left, width } = helpIconRef.current.getBoundingClientRect();
      const scrollOffset = window.scrollY;
      setTooltipPosition({
        top: top + scrollOffset,
        left: left + width + 10,
      });
    }
  };

  const todayYMD = () => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  };
  const getSelectedFromDate = () => {
    if (selectedTimePickerFromDate) {
      const [month, day, year] = selectedTimePickerFromDate.split('-');
      return `${year}-${month}-${day}`;
    }
    return todayYMD();
  };
  const getSelectedToDate = () => {
    if (selectedTimePickerToDate) {
      const [month, day, year] = selectedTimePickerToDate.split('-');
      return `${year}-${month}-${day}`;
    }
    return todayYMD();
  };

  useEffect(() => {
    // Wait until all three dropdowns have settled. Each dropdown fetches its
    if (!domainLoaded || !filterTypeLoaded || !timePickerLoaded) {
      return;
    }

    if (!selectedDomainWebsite || !selectedFilterType) {
      return;
    }

    const prevTime = prevTimePickerRef.current;
    prevTimePickerRef.current = selectedTimePicker;
    const justOpenedRangeModal =
      selectedTimePicker === -2 &&
      prevTime !== -2 &&
      (!selectedTimePickerFromDate || !selectedTimePickerToDate);
    if (justOpenedRangeModal) {
      return;
    }

    if (selectedTimePicker === -2 && (!selectedTimePickerFromDate || !selectedTimePickerToDate)) {
      return;
    }

    const requestData: any = {
      system_website_id: selectedDomainWebsite,
      filter_type: selectedFilterType,
      time: selectedTimePicker,
    };

    if (selectedTimePicker === -2) {
      requestData.from = selectedTimePickerFromDate;
      requestData.to = selectedTimePickerToDate;
    }

    setIsLoaderVisible(true);
    setTilesLoading(true);
    DashBoardApi.getdashboardsystemusage(requestData)
      .then((response) => {
        if (response.ok) {
          return response.json();
        }
        if (response.status === 401 || response.status === 400) {
          navigate('/login');
        }
        return null;
      })
      .then((data) => {
        if (data && data.status === 1) {
          setUsageData(data.data);
        } else {
          setUsageData(null);
        }
        setIsLoaderVisible(false);
        setTilesLoading(false);
        setFirstLoadDone(true);
      })
      .catch((error) => {
        console.log(error);
        setIsLoaderVisible(false);
        setTilesLoading(false);
        setFirstLoadDone(true);
      });
  }, [
    domainLoaded,
    filterTypeLoaded,
    timePickerLoaded,
    selectedDomainWebsite,
    selectedFilterType,
    selectedTimePicker,
    selectedTimePickerFromDate,
    selectedTimePickerToDate,
    navigate,
  ]);

  const renderTiles = () => {
    if (filterTypeUsesVisits(selectedFilterType)) {
      return (
        <Grid gutter="md">
          <Grid.Col xs={12} md={6}>
            <StatTile
              classes={classes}
              icon={<IconWorld size={22} />}
              iconColor="blue"
              label={filterTotalLabel(selectedFilterType)}
              value={formatNumber(usageData?.total_visit_count)}
              loading={tilesLoading}
            />
          </Grid.Col>
          <Grid.Col xs={12} md={6}>
            <StatTile
              classes={classes}
              icon={<IconEye size={22} />}
              iconColor="teal"
              label={filterDomainLabel(selectedFilterType)}
              value={formatNumber(usageData?.visit_domain_count)}
              loading={tilesLoading}
            />
          </Grid.Col>
        </Grid>
      );
    }

    if (selectedFilterType === '4') {
      return (
        <>
          <Text className={classes.sectionLabel} mb="xs">
            Account Totals
          </Text>
          <Grid gutter="md" mb="md">
            <Grid.Col xs={12} sm={6} md={4}>
              <StatTile
                classes={classes}
                icon={<IconHandClick size={22} />}
                iconColor="orange"
                label="Total Heatmap Clicks"
                value={formatNumber(usageData?.total_heatmap_count)}
                loading={tilesLoading}
                small
              />
            </Grid.Col>
            <Grid.Col xs={12} sm={6} md={4}>
              <StatTile
                classes={classes}
                icon={<IconArrowsVertical size={22} />}
                iconColor="grape"
                label="Total Scrollmap Views"
                value={formatNumber(usageData?.total_scrollmap_count)}
                loading={tilesLoading}
                small
              />
            </Grid.Col>
            <Grid.Col xs={12} sm={6} md={4}>
              <StatTile
                classes={classes}
                icon={<IconVideo size={22} />}
                iconColor="red"
                label="Total Recordings"
                value={formatNumber(usageData?.total_recording_count)}
                loading={tilesLoading}
                small
              />
            </Grid.Col>
          </Grid>

          <Text className={classes.sectionLabel} mb="xs">
            Domain Totals
          </Text>
          <Grid gutter="md">
            <Grid.Col xs={12} sm={6} md={4}>
              <StatTile
                classes={classes}
                icon={<IconHandClick size={22} />}
                iconColor="orange"
                label="Domain Heatmap Clicks"
                value={formatNumber(usageData?.heatmaps_domain_count)}
                loading={tilesLoading}
                small
              />
            </Grid.Col>
            <Grid.Col xs={12} sm={6} md={4}>
              <StatTile
                classes={classes}
                icon={<IconArrowsVertical size={22} />}
                iconColor="grape"
                label="Domain Scrollmap Views"
                value={formatNumber(usageData?.scrollmap_domain_count)}
                loading={tilesLoading}
                small
              />
            </Grid.Col>
            <Grid.Col xs={12} sm={6} md={4}>
              <StatTile
                classes={classes}
                icon={<IconVideo size={22} />}
                iconColor="red"
                label="Domain Recordings"
                value={formatNumber(usageData?.recordings_domain_count)}
                loading={tilesLoading}
                small
              />
            </Grid.Col>
          </Grid>
        </>
      );
    }

    let totalIcon = <IconChartArea size={22} />;
    let domainIcon = <IconChartArea size={22} />;
    let totalColor = 'blue';
    let domainColor = 'teal';
    let totalValue: any = 0;
    let domainValue: any = 0;

    if (selectedFilterType === '5') {
      totalIcon = <IconHandClick size={22} />;
      domainIcon = <IconHandClick size={22} />;
      totalColor = 'orange';
      domainColor = 'orange';
      totalValue = usageData?.total_heatmap_count;
      domainValue = usageData?.heatmaps_domain_count;
    } else if (selectedFilterType === '6') {
      totalIcon = <IconArrowsVertical size={22} />;
      domainIcon = <IconArrowsVertical size={22} />;
      totalColor = 'grape';
      domainColor = 'grape';
      totalValue = usageData?.total_scrollmap_count;
      domainValue = usageData?.scrollmap_domain_count;
    } else if (selectedFilterType === '7') {
      totalIcon = <IconVideo size={22} />;
      domainIcon = <IconVideo size={22} />;
      totalColor = 'red';
      domainColor = 'red';
      totalValue = usageData?.total_recording_count;
      domainValue = usageData?.recordings_domain_count;
    }

    return (
      <Grid gutter="md">
        <Grid.Col xs={12} md={6}>
          <StatTile
            classes={classes}
            icon={totalIcon}
            iconColor={totalColor}
            label={filterTotalLabel(selectedFilterType)}
            value={formatNumber(totalValue)}
            loading={tilesLoading}
          />
        </Grid.Col>
        <Grid.Col xs={12} md={6}>
          <StatTile
            classes={classes}
            icon={domainIcon}
            iconColor={domainColor}
            label={filterDomainLabel(selectedFilterType)}
            value={formatNumber(domainValue)}
            loading={tilesLoading}
          />
        </Grid.Col>
      </Grid>
    );
  };

  return (
    <>
      <VisiLoader VisiLoaderVisible={isLoaderVisible} />
      <HeaderMenu
        HeaderMenuValue={selectedHeaderWebsite}
        HeaderMenuOnChange={handleHeaderMenuComponentChange}
      />
      {showHelpModal &&
        ReactDOM.createPortal(
          <div style={{ zIndex: 9999 }}>
            <SelectTooltip
              isVisible={showHelpModal}
              setIsVisible={setShowHelpModal}
              id={helpTargetId}
              tooltipPosition={tooltipPosition}
            />
          </div>,
          document.body
        )}

      <Container size="xl" mb={10}>
        <Card shadow="sm" padding="sm" radius="md" withBorder>
          <Group position="apart" mb="sm" align="center">
            <Text className={classes.pageTitle}>
              SYSTEM USAGE{' '}
              <IconHelp
                ref={helpIconRef}
                size="1.1rem"
                color="red"
                style={{ marginBottom: -3, cursor: 'pointer' }}
                onClick={() => handleHelpClick(1)}
              />
            </Text>
          </Group>

          <Box className={classes.filterBar} mb="md">
            <Flex
              align="center"
              gap="md"
              wrap="wrap"
              justify="space-between"
            >
              <Flex align="center" gap="xs" wrap="wrap">
                <IconFilter size={16} />
                <Text className={classes.miniLabel}>Filter:</Text>
                <Box style={{ minWidth: 220, position: 'relative' }}>
                  {(!filterTypeLoaded || !firstLoadDone) && (
                    <Skeleton height={36} radius="sm" width={220} />
                  )}
                  <Box style={{ display: filterTypeLoaded && firstLoadDone ? 'block' : 'none' }}>
                    <SelectFilterType
                      SelectFilterTypeValue={selectedFilterType}
                      SelectFilterTypeOnChange={handleFilterTypeChange}
                    />
                  </Box>
                </Box>
              </Flex>
              <Flex align="center" gap="xs" wrap="wrap">
                <IconClock size={16} />
                <Box style={{ minWidth: 180, position: 'relative' }}>
                  {(!timePickerLoaded || !firstLoadDone) && (
                    <Skeleton height={36} radius="sm" width={180} />
                  )}
                  <Box style={{ display: timePickerLoaded && firstLoadDone ? 'block' : 'none' }}>
                    <SelectTimePicker
                      SelectTimePickerValue={selectedTimePicker}
                      SelectTimePickerOnChange={handleTimePickerChange}
                      SelectTimePickerFromDateOnChange={handleTimePickerFromDateChange}
                      SelectTimePickerToDateOnChange={handleTimePickerToDateChange}
                      SelectTestStartDate={getSelectedFromDate()}
                      SelectTestEndDate={getSelectedToDate()}
                      SelectedPage="system_usage"
                    />
                  </Box>
                </Box>
              </Flex>
            </Flex>
          </Box>

          <Box className={classes.domainBar} mb="md">
            <Flex align="center" gap="sm" wrap="wrap">
              <Text className={classes.miniLabel} weight={500}>
                Domain:
              </Text>
              <Box style={{ minWidth: 300, position: 'relative' }}>
                {(!domainLoaded || !firstLoadDone) && (
                  <Skeleton height={36} radius="sm" width={300} />
                )}
                <Box style={{ display: domainLoaded && firstLoadDone ? 'block' : 'none' }}>
                  <SelectMemberWebsiteList
                    SelectMemberWebsiteListValue={selectedDomainWebsite}
                    SelectMemberWebsiteListOnChange={handleDomainWebsiteChange}
                    SelectMemberWebsiteListWidth={300}
                    SelectMemberWebsiteListUsedIn="SystemUsage"
                    PageType="SystemUsage"
                  />
                </Box>
              </Box>
            </Flex>
          </Box>

          {renderTiles()}
        </Card>
      </Container>
    </>
  );
}
